import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const PORTAL_USER_UPDATED_EVENT = 'portal_user_updated';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
const EMOJI_OPTIONS = ['🙂', '😄', '😎', '🧠', '🚀', '⭐', '🔥', '💡', '🎯', '🎓'];

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const ProfileList = styled.dl`
  margin: 1.8rem 0 0;
  display: grid;
  grid-template-columns: 140px 1fr;
  row-gap: 0.9rem;
  column-gap: 1rem;
`;

const Label = styled.dt`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 600;
`;

const Value = styled.dd`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const EmojiSection = styled.div`
  margin-top: 1.4rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
`;

const EmojiTitle = styled.p`
  margin: 0 0 0.6rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 600;
`;

const EmojiGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const AvatarPreview = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.5);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 0.8rem;
`;

const EmojiButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 1px solid
    ${({ selected }) => (selected ? 'rgba(99, 102, 241, 0.9)' : 'rgba(148, 163, 184, 0.35)')};
  background: ${({ selected }) => (selected ? 'rgba(99, 102, 241, 0.2)' : 'transparent')};
  color: #e2e8f0;
  font-size: 1.25rem;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(99, 102, 241, 0.14);
  }
`;

const IconStatusMessage = styled.p`
  margin: 0.75rem 0 0;
  min-height: 1.2rem;
  color: ${({ error }) => (error ? '#fca5a5' : '#93c5fd')};
  font-size: 0.9rem;
`;

const InfoStatusMessage = styled.p`
  margin: 1rem 0 0;
  min-height: 1.2rem;
  color: ${({ error }) => (error ? '#fca5a5' : '#93c5fd')};
  font-size: 0.9rem;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.roles) ? parsed : null;
  } catch {
    return null;
  }
};

const resolveRoleLabel = (roles = []) => {
  if (roles.includes('super_admin')) return '슈퍼관리자';
  if (roles.includes('admin')) return '관리자';
  if (roles.includes('teacher')) return '강사';
  if (roles.includes('parent')) return '학부모';
  if (roles.includes('student')) return '학생';
  return '미지정';
};

const PortalProfilePage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [profile, setProfile] = useState(null);
  const [iconMessage, setIconMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!portalUser?.login_id) return;
      try {
        const data = await request({
          endpoint: `${BACKEND_BASE_URL}/api/auth/profile?login_id=${encodeURIComponent(portalUser.login_id)}`,
        });
        if (data?.profile) {
          setProfile(data.profile);
        }
      } catch (err) {
        setInfoMessage(err?.responseData?.message || '프로필 정보를 불러오지 못했습니다.');
      }
    };
    fetchProfile();
  }, [portalUser?.login_id, request]);

  const roles = profile?.roles || portalUser?.roles || [];
  const roleLabel = resolveRoleLabel(roles);
  const activeEmoji = profile?.profile_emoji || portalUser?.profile_emoji || '🙂';

  const handleSelectEmoji = async (emoji) => {
    if (!portalUser?.login_id) return;
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/auth/profile/emoji`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          profile_emoji: emoji,
        },
      });
      const savedEmoji = data?.profile?.profile_emoji || emoji;
      setProfile((prev) => ({ ...(prev || {}), profile_emoji: savedEmoji }));
      const mergedUser = { ...(portalUser || {}), profile_emoji: savedEmoji };
      sessionStorage.setItem(PORTAL_USER_KEY, JSON.stringify(mergedUser));
      window.dispatchEvent(new Event(PORTAL_USER_UPDATED_EVENT));
      setIconMessage('프로필 아이콘이 저장되었습니다.');
    } catch (err) {
      setIconMessage(err?.responseData?.message || '프로필 아이콘 저장에 실패했습니다.');
    }
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="PROFILE"
        title="내 프로필"
        description="이름·연락처와 포털에서 보이는 내 아이콘을 확인하고 바꿀 수 있어요."
      />
      <EmojiSection>
        <EmojiTitle>프로필 아이콘</EmojiTitle>
        <AvatarPreview>{activeEmoji}</AvatarPreview>
        <EmojiTitle>아이콘 선택</EmojiTitle>
        <EmojiGrid>
          {EMOJI_OPTIONS.map((emoji) => (
            <EmojiButton
              key={emoji}
              type="button"
              selected={activeEmoji === emoji}
              onClick={() => handleSelectEmoji(emoji)}
              disabled={loading}
              aria-label={`프로필 이모지 ${emoji}`}
            >
              {emoji}
            </EmojiButton>
          ))}
        </EmojiGrid>
        <IconStatusMessage error={/실패|오류|못/.test(iconMessage)}>{iconMessage}</IconStatusMessage>
      </EmojiSection>
      <ProfileList>
        <Label>이름</Label>
        <Value>{profile?.name || portalUser?.name || portalUser?.username || '-'}</Value>
        <Label>아이디</Label>
        <Value>{profile?.login_id || portalUser?.login_id || portalUser?.username || '-'}</Value>
        <Label>역할</Label>
        <Value>{roleLabel}</Value>
        <Label>연락처</Label>
        <Value>{profile?.phone || portalUser?.phone || portalUser?.phone_number || '-'}</Value>
        <Label>이메일</Label>
        <Value>{profile?.email || portalUser?.email || '-'}</Value>
      </ProfileList>
      <InfoStatusMessage error={/실패|오류|못/.test(infoMessage)}>{infoMessage}</InfoStatusMessage>
    </Wrapper>
  );
};

export default PortalProfilePage;
